import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useDatasetColumns } from '@/hooks/member-views/useDatasetColumns';
import { type DatasetState, type MemberDatasetView, useMemberDataset } from '@/hooks/member-views/useMemberDataset';
import type { MemberColumnDef, MemberDataset, MemberRowData } from '@/lib/member-views/memberDataset.types';
import { AlphaBar } from './AlphaBar';
import { DirectoryPagination } from './DirectoryPagination';
import { ACTIONS_COLUMN_ID, DirectoryTable } from './DirectoryTable';
import { GenerateReportButton } from './GenerateReportButton';
import { ResultsSummary } from './ResultsSummary';
import type { StateCopy } from './StateBoundary';
import { ViewSelect } from './ViewSelect';

export interface HeaderActionsContext {
  state: DatasetState;
  /** How many rows match the view and letter, across all pages. */
  rowCount: number;
  export: MemberDatasetView<MemberRowData>['export'];
}

const defaultHeaderActions = ({ state, rowCount, export: report }: HeaderActionsContext) => (
  <GenerateReportButton state={state} rowCount={rowCount} pending={report.pending} onGenerate={report.run} />
);

export interface MemberViewShellProps<TRow extends MemberRowData> {
  dataset: MemberDataset<TRow>;
  /** Replaces the dataset's generated columns, for a view that adds, drops or reorders them. */
  columns?: MemberColumnDef<TRow>[];
  /** Appends a column of per-row actions. */
  rowActions?: (row: TRow) => ReactNode;
  /**
   * The toolbar's trailing slot. Omitted means the Generate detailed report button; a view that
   * replaces it gets the same export to hand its own controls.
   */
  headerActions?: (ctx: HeaderActionsContext) => ReactNode;
  /** Replaces the default "no patients" copy for a view whose rows mean something else. */
  emptyState?: StateCopy;
  /** Swaps in a richer cell for the named columns, keeping their header and sorting. */
  cellOverrides?: Partial<Record<string, (row: TRow) => ReactNode>>;
  /** A toolbar slot between the view select and the header actions, held for search. */
  toolbarExtras?: ReactNode;
}

/**
 * MemberViewShell — everything a Member Directory view shows around its rows: the view select,
 * the A–Z bar, the results line, the table and the pager. A view passes its dataset and whatever
 * it changes; the rest is the same for every view.
 */
export function MemberViewShell<TRow extends MemberRowData>({
  dataset,
  columns,
  rowActions,
  headerActions = defaultHeaderActions,
  emptyState,
  cellOverrides,
  toolbarExtras,
}: MemberViewShellProps<TRow>) {
  const { t } = useTranslation('providers');
  const generated = useDatasetColumns(dataset).all;

  const withCells = (columns ?? generated).map((column) => {
    const render = column.id ? cellOverrides?.[column.id] : undefined;
    return render ? { ...column, cell: ({ row }: { row: { original: TRow } }) => render(row.original) } : column;
  });
  const tableColumns: MemberColumnDef<TRow>[] = rowActions
    ? [
        ...withCells,
        {
          id: ACTIONS_COLUMN_ID,
          header: t('memberViews.columns.actions'),
          enableSorting: false,
          cell: ({ row }) => rowActions(row.original),
        },
      ]
    : withCells;

  const view = useMemberDataset(dataset, { columns: tableColumns });
  const letter = view.alpha.selected;
  // A letter nobody's name starts with says so, whatever the view's own empty copy is.
  const empty: StateCopy | undefined =
    letter === 'all'
      ? emptyState
      : {
          title: t('memberViews.states.noLetter.title', { letter, isOther: String(letter === '#') }),
          description: t('memberViews.states.noLetter.body'),
        };

  return (
    <div className="@container/md flex min-w-0 flex-col gap-4">
      <div className="flex flex-col gap-4 @3xl/md:flex-row @3xl/md:items-center @3xl/md:gap-6">
        <ViewSelect value={dataset.key} />
        {toolbarExtras && <div className="min-w-0 @3xl/md:w-80">{toolbarExtras}</div>}
        <div className="flex @3xl/md:ml-auto">
          {headerActions({ state: view.state, rowCount: view.filteredRows.length, export: view.export })}
        </div>
      </div>
      <AlphaBar alpha={view.alpha} state={view.state} />
      <ResultsSummary table={view.table} state={view.state} />
      <DirectoryTable table={view.table} state={view.state} onRetry={view.retry} empty={empty} />
      <DirectoryPagination table={view.table} state={view.state} />
    </div>
  );
}
